import { useEffect, useLayoutEffect, useMemo, useState } from 'react';

import type { RefObject } from 'react';

const ELEMENT_HEIGHT = 70;
const OVER_SCAN = 5;
const SCROLLING_DELAY = 100;

export const useVirtualScroll = (
  containerRef: RefObject<HTMLDivElement>,
  listLength: number,
  pageSize?: number
) => {
  const [isScrolling, setIsScrolling] = useState(false);
  const [scrollTop, setScrollTop] = useState(0);
  const [scrollElementHeight, setScrollElementHeight] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(() =>
    pageSize ? Math.ceil(listLength / pageSize) : 0
  );

  useLayoutEffect(() => {
    pageSize && setTotalPages(Math.ceil(listLength / pageSize));
  }, [listLength, pageSize]);

  useEffect(() => {
    const scrollElement = containerRef.current;
    if (!scrollElement) {
      return;
    }

    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    const handleScroll = () => {
      setIsScrolling(true);
      if (typeof timeoutId === 'number') {
        clearTimeout(timeoutId);
      }
      timeoutId = setTimeout(() => {
        setIsScrolling(false);
      }, SCROLLING_DELAY);
    };

    handleScroll();

    scrollElement.addEventListener('scroll', handleScroll);

    return () => {
      if (typeof timeoutId === 'number') {
        clearTimeout(timeoutId);
      }
      scrollElement.removeEventListener('scroll', handleScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useLayoutEffect(() => {
    const scrollElement = containerRef.current;
    if (!scrollElement) {
      return;
    }

    const handleScroll = () => {
      const { scrollTop } = scrollElement;
      setScrollTop(scrollTop);
    };

    handleScroll();

    scrollElement.addEventListener('scroll', handleScroll);

    return () => scrollElement.removeEventListener('scroll', handleScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useLayoutEffect(() => {
    const scrollElement = containerRef.current;
    if (!scrollElement) {
      return;
    }

    const resizeObserver = new ResizeObserver(([entry]) => {
      if (!entry) {
        return;
      }
      const height =
        entry.borderBoxSize[0]?.blockSize ??
        entry.target.getBoundingClientRect().height;

      setScrollElementHeight(height);
    });

    resizeObserver.observe(scrollElement);

    return () => {
      resizeObserver.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const virtualItems = useMemo(() => {
    let startIndex;
    let endIndex;

    if (!pageSize) {
      const rangeTop = scrollTop;
      const rangeBottom = scrollTop + scrollElementHeight; //высота контейнера

      startIndex = Math.floor(rangeTop / ELEMENT_HEIGHT);
      endIndex = Math.ceil(rangeBottom / ELEMENT_HEIGHT);

      startIndex = Math.max(0, startIndex - OVER_SCAN);
      endIndex = Math.min(listLength - 1, endIndex + OVER_SCAN);
    } else {
      startIndex = 0;
      if (listLength === 0) {
        endIndex = -1;
      } else {
        endIndex =
          currentPage === totalPages
            ? (listLength % pageSize) - 1
            : pageSize - 1;
      }
    }

    const virtualItems = [];

    for (let index = startIndex; index <= endIndex; index++) {
      let elementIndex;
      if (!pageSize) {
        elementIndex = index;
      } else {
        elementIndex =
          currentPage > 1 ? (currentPage - 1) * pageSize + index : index;
      }

      virtualItems.push({
        index: elementIndex,
        offsetTop: index * ELEMENT_HEIGHT,
      });
    }
    return virtualItems;
  }, [
    currentPage,
    listLength,
    pageSize,
    scrollElementHeight,
    scrollTop,
    totalPages,
  ]);

  return {
    isPageScroll: !!pageSize,
    virtualItems,
    isScrolling,
    scrollElementHeight,
    currentPage,
    setCurrentPage,
    totalPages,
  };
};
